import { Component, effect, inject, signal } from '@angular/core'
import { ModalHeaderComponent } from '../modal-header/modal-header.component'
import { ModalFooterComponent } from '../modal-footer/modal-footer.component'
import { ModalBodyComponent } from '../modal-body/modal-body.component'
import { RightAlignComponent } from '../../right-align/right-align.component'
import { ImportIntervalUsageTableComponent } from '../../table/import-interval-usage-table.component'
import { combineLatest, firstValueFrom, map, Observable, of } from 'rxjs'
import { IntervalUsage } from 'src/app/shared/models/intervalUsage'
import { FileInputComponent } from '../../file-input/file-input.component'
import { ReferenceDataService } from '../../reference-data/reference-data.service'
import * as Papa from 'papaparse'
import { IntervalUsageService } from '../../interval-usage/interval-usage.service'
import { isValid } from 'date-fns'
import { est, sanitizeDateString } from 'src/app/shared/utilities/date'

@Component({
  imports: [
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    RightAlignComponent,
    ImportIntervalUsageTableComponent,
    FileInputComponent,
  ],
  selector: 'app-interval-usage-import-modal',
  styles: `
    .error {
      color: red;
      font-size: 0.65rem;
    }
  `,
  template: `
    <div app-modal-header>Import</div>
    <div app-modal-body>
      <div app-right-align>
        <button class="action-btn" (click)="onDownloadTemplate()">
          Download Template
        </button>
        <div
          app-file-input
          [accept]="'text/csv'"
          [skipParse]="true"
          (changeFile)="onUpload($event)"
        >
          Upload File
        </div>
      </div>
      <app-import-interval-usage-table
        [data]="data()"
        [loading]="loading()"
        [error]="error()"
      ></app-import-interval-usage-table>
    </div>
    <div app-modal-footer>
      <button class="action-btn" (click)="onCancel()()">Cancel</button>
      <button
        class="action-btn primary"
        (click)="onImport()(data())"
        [disabled]="loading() || error() || !data().length"
      >
        Import
      </button>
    </div>
  `,
})
export class IntervalUsageImportModalComponent {
  referenceDataService = inject(ReferenceDataService)
  intervalUsageService = inject(IntervalUsageService)

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onImport = signal((_data: Partial<IntervalUsage>[]): void => void 0)
  protected onCancel = signal((): void => void 0)
  protected loading = signal(false)
  protected error = signal('')
  protected fileData = signal<File | undefined>(undefined)
  protected data = signal<Partial<IntervalUsage>[]>([])

  constructor() {
    effect((async () => {
      if(this.fileData()) {
        this.startLoadingSequence()
        const csvData = await firstValueFrom(this.loadCSV(this.fileData()))
        const csvWithMarketInterval = this.withMarketInterval(csvData)
        const csvWithMeasurements = this.withMeasurmentValue(csvWithMarketInterval)
        const csvWithServicePoints = await firstValueFrom(this.withServicePointUids(csvWithMeasurements))
        try {
          this.validate(csvWithServicePoints)
          this.endLoadingSequence(this.pickFields(csvWithServicePoints as Parameters<typeof this.pickFields>[0]))
        } catch (err) {
          this.endLoadingSequence(undefined, err as string)
        }
      }
    }))
  }

  startLoadingSequence() {
    this.loading.set(true)
    this.error.set('')
  }

  loadCSV(file: File | undefined): Observable<Record<string, unknown>[]> {
    return new Observable((observer) => {
      const rows: Partial<IntervalUsage>[] = []
      if (file) {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          step: (results: any) => {
            if (results.errors?.length) {
              // eslint-disable-next-line no-console
              results.errors.forEach(console.error)
              observer.error(
                'The file selected is invalid, please correct the file and retry',
              )
            } else {
              rows.push({ ...results.data })
            }
          },
          complete: () => observer.next(rows),
        })
      }
    })
  }

  withMarketInterval(
    records: Record<string, unknown>[],
  ): ((typeof records)[number] & { market_interval?: Date | undefined })[] {
    const MARKET_INTERVAL_DISPLAY = 'market_interval'
    const MARKET_INTERVAL = 'market_interval'
    return records.map((r) => {
      if (r[MARKET_INTERVAL]) {
        try {
          const originalString = (r[MARKET_INTERVAL] ?? r[MARKET_INTERVAL_DISPLAY]) as string
          const sanitizedString = sanitizeDateString(r[MARKET_INTERVAL] as string)
          const parsedMarketInterval = est(sanitizedString)
          r[MARKET_INTERVAL] = isValid(parsedMarketInterval) ? parsedMarketInterval : originalString
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(
            `Could not parse ${MARKET_INTERVAL}: ${r[MARKET_INTERVAL]}`,
          )
        }
      }
      return r
    })
  }

  withMeasurmentValue(
    records: Record<string, unknown>[],
  ): ((typeof records)[number] & { measurement_value?: string | undefined })[] {
    const MEASUREMENT_VALUE_DISPLAY = 'Measurement Value'
    const MEASUREMENT_VALUE = 'measurement_value'
    return records.map((r) => {
      if (r[MEASUREMENT_VALUE] || r[MEASUREMENT_VALUE_DISPLAY]) {
        try {
          const originalString = (r[MEASUREMENT_VALUE] ?? r[MEASUREMENT_VALUE_DISPLAY]) as string
          const [integer, decimal] = originalString.split('.')
          r[MEASUREMENT_VALUE] = Number(`${integer}.${(decimal ?? '0').slice(0, 8)}`)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(
            `Could not parse ${MEASUREMENT_VALUE}: ${r[MEASUREMENT_VALUE]}`,
          )
        }
      }
      return r
    })
  }


  withServicePointUids(records: Record<string, unknown>[]): Observable<
    ((typeof records)[number] & {
      service_point_id?: string;
      service_point_uid?: string;
    })[]
  > {
    const SERVICE_POINT_DISPLAY = 'Service Point'
    const SERVICE_POINT_ID = 'service_point_id'
    const SERVICE_POINT_UID = 'service_point_uid'

    const providedServicePoints: Set<string> = new Set(
      records
        .filter((r) => {
          const provided = r[SERVICE_POINT_ID] ?? r[SERVICE_POINT_DISPLAY]
          const rightType = typeof provided === 'string'
          if (!provided || !rightType) {
            // eslint-disable-next-line no-console
            console.error(
              `Could not parse ${SERVICE_POINT_ID}: ${r[SERVICE_POINT_ID]}`,
            )
          }
          return provided && rightType
        })
        .map((r) => (r[SERVICE_POINT_ID] as string)!.trim()),
    ) as Set<string>

    const servicePointRefData$ = this.referenceDataService.servicePoints(
      { pageNumber: 1, pageSize: 1000 },
      [
        {
          name: SERVICE_POINT_ID,
          values: [
            {
              operation: 'in=',
              value: [...providedServicePoints.values()].join(','),
            },
          ],
        },
      ],
    )

    return combineLatest([of(records), servicePointRefData$]).pipe(
      map(([recs, refs]) =>
        recs.map((r) => {
          const servicePointId: string = r[SERVICE_POINT_ID] as string
          if (!servicePointId) {
            // eslint-disable-next-line no-console
            console.error(
              `Could not parse ${SERVICE_POINT_ID}: ${r[SERVICE_POINT_ID]}`,
            )
          }
          const servicePointUid = refs.find(
            (ref) => ref.service_point_id === servicePointId.trim(),
          )?.service_point_uid
          if (!servicePointUid) {
            // eslint-disable-next-line no-console
            console.error(
              `Could not find mathing ${SERVICE_POINT_ID}: ${r[SERVICE_POINT_ID]}`,
            )
          }
          r[SERVICE_POINT_ID] = servicePointId.trim()
          r[SERVICE_POINT_UID] = servicePointUid
          return r
        }),
      ),
    )
  }

  validate(
    records: Record<string, unknown>[],
  ): records is Pick<
    IntervalUsage,
    | 'service_point_id'
    | 'service_point_uid'
    | 'market_interval'
    | 'measurement_value'
  >[] {
    const SERVICE_POINT_UID = 'service_point_uid'
    const SERVICE_POINT_ID = 'service_point_id'
    const MARKET_INTERVAL = 'market_interval'
    const MEASUREMENT_VALUE = 'measurement_value'
    const error: string | undefined = records
      .map((p) => {
        if (!p[SERVICE_POINT_ID] || !p[SERVICE_POINT_UID]) {
          return `Service point ${p[SERVICE_POINT_ID]} was not found. Please ensure it's existence and try again`
        }
        if (!p[MARKET_INTERVAL]) {
          return 'Market Interval must be provided. Please correct this and try again'
        }
        if (!p[MEASUREMENT_VALUE]) {
          return 'Measurement Value must be provided. Please correct this and try again'
        }
        if (!isValid(p[MARKET_INTERVAL])) {
          return `Market Interval ${p[MARKET_INTERVAL]} is not a valid date. Please correct this and try again`
        }
        if (isNaN(Number(p[MEASUREMENT_VALUE] as string))) {
          return `Measurement Value ${p[MEASUREMENT_VALUE]} is not a number. Please correct this and try again`
        }
      })
      .find(Boolean)
    if (error) {
      throw error
    }
    return true
  }

  pickFields(records: Pick<
    IntervalUsage,
    | 'service_point_id'
    | 'service_point_uid'
    | 'market_interval'
    | 'measurement_value'
  >[]): typeof records {
    return records.map(r => ({
      service_point_id: r.service_point_id,
      service_point_uid: r.service_point_uid,
      market_interval: r.market_interval,
      measurement_value: r.measurement_value,
    }))
  }

  endLoadingSequence(data?: Partial<IntervalUsage>[], err?: string) {
    this.data.set(data ?? [])
    this.loading.set(false)
    this.error.set(err ?? '')
  }

  init(overrides: Partial<{
    onImport: (data: Partial<IntervalUsage>[]) => void
    onCancel: () => void
  }> = {}): void {
    this.onImport.update(v => overrides.onImport ?? v)
    this.onCancel.update(v => overrides.onCancel ?? v)
  }

  onUpload(file: File | undefined) {
    this.fileData.set(file)
  }

  async onDownloadTemplate() {
    await firstValueFrom(this.intervalUsageService.downloadImportTemplate())
  }
}
